import styled, { css, keyframes } from 'styled-components';

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: 'Share Tech Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
`;

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ModalInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 14px;

  span:first-child {
    color: rgba(255, 255, 255, 0.5);
  }

  span:last-child {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }
`;

export const AddressLink = styled.a`
  color: rgba(155, 89, 182, 0.8);
  text-decoration: none;
  font-family: 'Share Tech Mono', monospace;
  transition: color 0.2s;

  &:hover {
    color: rgba(155, 89, 182, 1);
    text-decoration: underline;
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const InputLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const InputLabel = styled.label`
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
`;

export const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
`;

export const InputWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid
    ${(props) => (props.$hasError ? 'rgba(231, 76, 60, 0.5)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 10px;
  transition: border-color 0.3s;

  &:focus-within {
    border-color: ${(props) =>
      props.$hasError ? 'rgba(231, 76, 60, 0.7)' : 'rgba(155, 89, 182, 0.5)'};
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    color: #fff;
    font-family: 'Share Tech Mono', monospace;
    font-size: 18px;
    font-weight: bold;
    text-align: right;
    outline: none;

    &::placeholder {
      color: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
      opacity: 0.6;
    }

    @media (max-width: 600px) {
      font-size: 15px;
    }
  }
`;

export const MaxButton = styled.button`
  padding: 4px 8px 1px;
  border-radius: 4px;
  background: rgba(155, 89, 182, 0.15);
  border: 1px solid rgba(155, 89, 182, 0.3);
  color: rgba(155, 89, 182, 0.8);
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  flex-shrink: 0;

  &:hover {
    background: rgba(155, 89, 182, 0.25);
    border-color: rgba(155, 89, 182, 0.5);
  }
`;

export const UsdValue = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  text-align: right;
  color: rgba(255, 255, 255, 0.3);
`;

export const PresetButtonsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const PresetButton = styled.button`
  flex: 1;
  min-width: 50px;
  padding: 8px 10px 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Share Tech Mono', monospace;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(155, 89, 182, 0.3);
    color: #fff;
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const WalletsToCopy = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 14px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`;

export const ModalHint = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: rgba(155, 89, 182, 0.6);
  text-align: center;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const ActionButton = styled.button<{ $disabled?: boolean; $isLoading?: boolean }>`
  display: block;
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, rgba(155, 89, 182, 0.7), rgba(52, 152, 219, 0.7));
  border: none;
  border-radius: 10px;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 2px;
  box-shadow: 0 0 20px rgba(155, 89, 182, 0.2);
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s;

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}

  ${({ $disabled }) =>
    $disabled
      ? css`
          opacity: 0.45;
          cursor: not-allowed;
        `
      : css`
          &:hover {
            opacity: 0.9;
            background: linear-gradient(135deg, rgba(155, 89, 182, 0.8), rgba(52, 152, 219, 0.8));
            box-shadow:
              0 0 30px rgba(155, 89, 182, 0.5),
              0 0 60px rgba(52, 152, 219, 0.3);
          }
        `}

  @media (max-width: 600px) {
    padding: 10px;
    font-size: 13px;
  }
`;

export const WarningBox = styled.div`
  padding: 10px 14px;
  background: rgba(241, 196, 15, 0.1);
  border: 1px solid rgba(241, 196, 15, 0.3);
  border-radius: 8px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: rgba(241, 196, 15, 0.9);
  text-align: center;
`;
